import { useCallback, useContext, useMemo } from "react";
import { SetURLSearchParams } from "react-router-dom";
import { Context as PaginationContext } from "../contexts/PaginationContext";

export function usePaginationData(searchParams: URLSearchParams, maxPages: number) {
    const paginationData = useContext(PaginationContext)

    return useMemo(() => {

        if(maxPages < 1) {
            return {
                currentPage: 1,
                recordsPerPage: paginationData.recordsPerPage
            }
        }
        
        let currentPage: string | number = searchParams.get('page') ?? ""
    
        if(currentPage.length === 0) {
            currentPage = 1
        }
        else {
            currentPage = parseInt(currentPage)
            if(isNaN(currentPage)) {
                currentPage = 1
            }
            else {
                if(maxPages > 0 && currentPage > maxPages) {
                    currentPage = maxPages
                }
            }
        }
    
        // Records Per Page
        let recordsPerPage: string | number = searchParams.get('rpp') ?? ""
        if(recordsPerPage.length === 0) {
            recordsPerPage = paginationData.recordsPerPage
        }
        else {
            recordsPerPage = parseInt(recordsPerPage)
            if(isNaN(recordsPerPage) || !paginationData.recordsPerPageList.includes(recordsPerPage)) {
                recordsPerPage = paginationData.recordsPerPage
            }
        }
    
        return {
            currentPage,
            recordsPerPage
        }
    }, [searchParams, maxPages, paginationData])
}

export function useModifyPage(setSearchParams: SetURLSearchParams, pagination: ReturnType<typeof usePaginationData>) {
    return useCallback((page: number) => {
        setSearchParams(prev => {
            prev.set("page", page.toString())
            // Use this, because URL can looks like this:
            // dashboard/cars?page=2&rpp=asd
            prev.set("rpp", pagination.recordsPerPage.toString())
            return prev
        })
    }, [ setSearchParams, pagination ])
}

export function useModifyRecordsPerPage(setSearchParams: SetURLSearchParams) {
    return useCallback((rpp: number) => {
        setSearchParams(prev => {
            prev.set("page", "1")
            prev.set("rpp", rpp.toString())
            return prev
        })
    }, [ setSearchParams ])
}

export function useSetSearchUrlParams(setSearchParams: SetURLSearchParams) {
    return useCallback((searchParameters: Object) => {
        setSearchParams(prev => {
            Object.entries(searchParameters).forEach(([key, value]) => {
                key = key.toLowerCase()

                if(value != null && value.toString().length > 0) {
                    prev.set(key, value.toString())
                }
                else {
                    prev.delete(key)
                }
            })

            prev.set("page", "1")

            return prev
        })
    }, [setSearchParams])
}